import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Chart as ChartJS,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Filler,
  Title,
  SubTitle,
  CategoryScale,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Bar, Line } from 'react-chartjs-2';
import { ExternalLink, Plus, Minus, Trophy } from 'lucide-react';
import { urlParams } from '../utils/urlParams';
import { loadPerformanceData, isPerformanceDataEnabled } from '../utils/performanceDataLoader';
import { buildHighlights, buildLowlights, hasPerformanceSeries, buildFullResults, isClipMyHorseOnlyNoScore, CompetitionHistoryScope } from '../utils/performanceTransforms';
import { PerformanceData, PerformanceListItem, UnifiedClassEntry, UnifiedRiderHistory } from '../types/performance';
import { HorseOwnersHistory } from './HorseOwnersHistory';
import { HorseRead } from '../types/api';
import { formatPersonName } from '../utils/personName';

ChartJS.register(TimeScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend, Filler, Title, SubTitle, CategoryScale);

const COLOR_PRIMARY = '#7DCD85';
const COLOR_SECONDARY = '#132B64';
const COLOR_PRIMARY_DARK = '#2F7A38';
const MONTHLY_CLEAR_LABEL = 'Horse clear round rate, month alone';
const COLOR_NEUTRAL = '#504F4F';

// Period filter types and utilities
type PeriodFilter = '6m' | '1y' | '3y' | 'max';

const PERIOD_LABELS: Record<PeriodFilter, string> = {
  '6m': '6M',
  '1y': '1Y',
  '3y': '3Y',
  'max': 'Max',
};

const getPeriodCutoffDate = (period: PeriodFilter): Date | null => {
  if (period === 'max') return null;
  const now = new Date();
  switch (period) {
    case '6m':
      return new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
    case '1y':
      return new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
    case '3y':
      return new Date(now.getFullYear() - 3, now.getMonth(), now.getDate());
    default:
      return null;
  }
};

const filterEntriesByPeriod = (entries: UnifiedClassEntry[], period: PeriodFilter): UnifiedClassEntry[] => {
  const cutoff = getPeriodCutoffDate(period);
  if (!cutoff) return entries;
  return entries.filter((e) => {
    if (!e.unified_class_date) return false;
    const entryDate = new Date(e.unified_class_date);
    return entryDate >= cutoff;
  });
};

const filterMonthlyDataByPeriod = <T,>(
  months: string[],
  data: T[],
  period: PeriodFilter
): { months: string[]; data: T[] } => {
  const cutoff = getPeriodCutoffDate(period);
  if (!cutoff) return { months, data };
  const cutoffMonth = cutoff.toISOString().slice(0, 7); // YYYY-MM format
  const filteredIndices: number[] = [];
  months.forEach((m, idx) => {
    if (m >= cutoffMonth) {
      filteredIndices.push(idx);
    }
  });
  return {
    months: filteredIndices.map((i) => months[i]),
    data: filteredIndices.map((i) => data[i]),
  };
};

const filterPerformanceData = (data: PerformanceData, period: PeriodFilter): PerformanceData => {
  const filteredEntries = filterEntriesByPeriod(data.classEntries, period);
  const { months: filteredMonths, data: filteredMaxHeight } = filterMonthlyDataByPeriod(
    data.months,
    data.maxObstacleHeight,
    period
  );
  const { data: filteredPrize } = filterMonthlyDataByPeriod(data.months, data.sumPrizeInEuro, period);
  const { data: filteredMonthlyClearRound } = filterMonthlyDataByPeriod(
    data.months,
    data.monthlyClearRoundRate,
    period
  );
  const { data: filteredMonthlyNumRounds } = filterMonthlyDataByPeriod(
    data.months,
    data.monthlyNumRounds,
    period
  );
  const { data: filteredClearRound } = filterMonthlyDataByPeriod(
    data.months,
    data.meanClearRoundMovingAvg,
    period
  );
  const { data: filteredClassClearRound } = filterMonthlyDataByPeriod(
    data.months,
    data.meanClassClearRoundRateMovingAvg,
    period
  );
  return {
    ...data,
    classEntries: filteredEntries,
    months: filteredMonths,
    maxObstacleHeight: filteredMaxHeight,
    sumPrizeInEuro: filteredPrize,
    monthlyClearRoundRate: filteredMonthlyClearRound,
    monthlyNumRounds: filteredMonthlyNumRounds.map((n) => n ?? 0),
    meanClearRoundMovingAvg: filteredClearRound,
    meanClassClearRoundRateMovingAvg: filteredClassClearRound,
  };
};

interface HorsePerformanceSectionProps {
  horseId?: string | null;
  horseName?: string | null;
  horse?: HorseRead | null;
  onMaxLevelChange?: (heightCm: number | null) => void;
}

type LoadState = 'idle' | 'loading' | 'ready' | 'error' | 'disabled';

interface RiderEntry {
  name: string;
  startDate?: string;
  endDate?: string;
  isCurrent: boolean;
}

const DAY_MS = 1000 * 60 * 60 * 24;
const MIN_RIDER_DAY_GAP = 90;
const MAX_RIDER_DAY_GAP = 180;

const formatMonthYear = (value?: string) => {
  if (!value) return '';
  const [y, m] = value.split('-').map(Number);
  if (!y || !m) return value;
  const date = new Date(Date.UTC(y, m - 1, 1));
  return date.toLocaleString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
};

const buildRiderHistory = (data: PerformanceData): RiderEntry[] => {
  // Use unified_rider_history from the API if available
  if (data.riderHistory && data.riderHistory.length > 0) {
    const periods: RiderEntry[] = data.riderHistory.map((rider: UnifiedRiderHistory, idx: number) => {
      const name = formatPersonName(rider.unified_rider_name) || 'Unknown Rider';
      return {
        name,
        startDate: rider.unified_first_ride_date ?? undefined,
        endDate: rider.unified_last_ride_date ?? undefined,
        isCurrent: idx === data.riderHistory.length - 1,
      };
    });
    return periods;
  }

  // Fallback: build from class entries
  const sorted = [...data.classEntries]
    .filter((e) => e.unified_rider_name)
    .sort((a, b) => {
      const aDate = a.unified_class_date ? new Date(a.unified_class_date).getTime() : 0;
      const bDate = b.unified_class_date ? new Date(b.unified_class_date).getTime() : 0;
      return aDate - bDate;
    });

  const periods: RiderEntry[] = [];
  let activeName: string | null = null;
  let start: string | null = null;
  let end: string | null = null;

  const pushPeriod = () => {
    if (!activeName || !start || !end) return;
    periods.push({ name: activeName, startDate: start, endDate: end, isCurrent: false });
  };

  sorted.forEach((entry) => {
    const riderRaw = entry.unified_rider_name?.toString().trim();
    if (!riderRaw) return;
    const rider = formatPersonName(riderRaw);
    if (!rider) return;
    const date = entry.unified_class_date;

    if (activeName === rider) {
      end = date;
      return;
    }

    // rider changed
    pushPeriod();
    activeName = rider;
    start = date;
    end = date;
  });

  pushPeriod();

  if (periods.length === 0) {
    return [];
  }

  const lastIdx = periods.length - 1;
  periods[lastIdx] = { ...periods[lastIdx], isCurrent: true };

  return periods;
};

const spreadRiderDates = (dates: Date[], minGapDays: number, maxGapDays: number) => {
  const adjusted: Date[] = [];
  dates.forEach((raw, idx) => {
    if (idx === 0) {
      adjusted.push(raw);
      return;
    }
    const prev = adjusted[idx - 1];
    const minAllowed = prev.getTime() + minGapDays * DAY_MS;
    const maxAllowed = prev.getTime() + maxGapDays * DAY_MS;
    // Clamp to min/max gap range
    const clamped = Math.min(Math.max(raw.getTime(), minAllowed), maxAllowed);
    adjusted.push(new Date(clamped));
  });
  return adjusted;
};

const formatLongDate = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const PerformanceCard: React.FC<{ title?: string; children: React.ReactNode; alignCenter?: boolean; srLabel?: string }> = ({ title, children, alignCenter, srLabel }) => (
  <div className="p-3" aria-label={srLabel}>
    {title && (
      <div className="flex items-center justify-between mb-3">
        <h3 className={`text-base font-semibold text-slate-900 ${alignCenter ? 'text-center w-full' : ''}`}>{title}</h3>
      </div>
    )}
    {children}
  </div>
);

// Competition Results Table - matches Figma design with color-coded rows
const CompetitionTableRow: React.FC<{
  item: PerformanceListItem;
  isFirst?: boolean;
  isLast?: boolean;
}> = ({ item, isFirst, isLast }) => {
  const [expanded, setExpanded] = React.useState(false);

  // Determine row color based on rank/success
  const getRowBg = () => {
    if (item.successCategory === 3) {
      return 'bg-[#edafa8]'; // Red/pink for bad results — check before rank
    }
    if (item.successCategory === 1 || (item.rank !== null && item.rank !== undefined && item.rank <= 3)) {
      return 'bg-[#cfe0c1]'; // Green for good results
    }
    return 'bg-white'; // Neutral
  };

  const headline = item.subtitle || item.title;
  const longDate = formatLongDate(item.dateRaw) || item.date;
  const eventCode = item.eventCode || '—';
  const _competitionName = item.competitionName || '—';
  const classHeight =
    item.obstacleHeight !== null && item.obstacleHeight !== undefined ? `${item.obstacleHeight}m` : '—';
  const rankLabel =
    item.rank !== null && item.rank !== undefined
      ? item.numEntries
        ? `${item.rank}/${item.numEntries}`
        : String(item.rank)
      : '—';
  const prizeLabel =
    item.prizeEuro !== null && item.prizeEuro !== undefined
      ? `€${Math.round(item.prizeEuro).toLocaleString()}`
      : '—';
  const scoreLabel = item.score || '—';
  const riderLabel = item.rider || '—';
  const expandedBg =
    item.successCategory === 3
      ? '#F0DDDB' // bad
      : item.successCategory === 1
        ? '#e3e9de' // good (unchanged)
        : '#F6F6F6'; // neutral

  return (
    <>
      {/* Data row */}
      <div
        className={`${getRowBg()} py-2 flex items-center text-[12px] text-[#233454] ${isFirst ? 'rounded-t' : ''} ${isLast && !expanded ? 'rounded-b' : ''} ${!isLast ? 'border-b border-white/60' : ''}`}
      >
        <button
          aria-label={expanded ? 'Collapse' : 'Expand'}
          className="w-[30px] flex items-center justify-center text-[#233454] hover:text-[#132B64]"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? <Minus size={12} /> : <Plus size={12} />}
        </button>
        <div
          className="grid flex-1 min-w-0 items-center gap-1"
          style={{ gridTemplateColumns: '0.6fr 0.6fr 0.2fr 0.9fr' }}
        >
          <span className="truncate" title={item.date}>{item.date}</span>
          <span className="truncate" title={item.obstacleHeight !== null && item.obstacleHeight !== undefined ? String(item.obstacleHeight) : '—'}>
            {item.obstacleHeight ?? '—'}
          </span>
          <span className="truncate" title={item.rank !== null && item.rank !== undefined ? String(item.rank) : '—'}>
            {item.rank ?? '—'}
          </span>
          <span className="truncate" title={item.score || '—'}>{item.score || '—'}</span>
        </div>
        <div className="w-[36px] flex justify-start pr-0">
          {item.videoUrl ? (
            <a
              className="text-blue-600 hover:text-blue-800 inline-flex items-center"
              href={item.videoUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/icons/youtube.svg" alt="Video" className="h-4 w-4" />
            </a>
          ) : (
            <div className="h-4 w-4" />
          )}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div
          className={`px-[30px] py-4 ${isLast ? 'rounded-b' : ''}`}
          style={{ backgroundColor: expandedBg }}
        >
          <div className="text-[#233454] text-[12px] space-y-1">
            <div className="flex flex-wrap items-center gap-2 leading-snug">
              <span className="font-semibold" title={headline}>{headline}</span>
              <span className="text-[11px]" title={longDate}>{longDate}</span>
            </div>
            <div className="flex flex-wrap items-center gap-1 text-[11px] leading-snug">
              <span className="font-medium">{eventCode}</span>
              <span>•</span>
              <span>{classHeight}</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] leading-snug">
              <span>
                Rider: <span className="font-semibold">{riderLabel}</span>
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] leading-snug">
              <span>
                Score: <span className="font-semibold">{scoreLabel}</span>
              </span>
              <span>
                Rank: <span className="font-semibold">{rankLabel}</span>
              </span>
              <span>
                Prize: <span className="font-semibold">{prizeLabel}</span>
              </span>
            </div>
            {/* Competition link */}
            {item.classId && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    urlParams.navigateToCompetitionDetail(item.classId!);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-[#132B64] hover:bg-[#0f2350] rounded-full transition-colors"
                >
                  <Trophy size={11} />
                  View Competition
                </button>
              </div>
            )}
            {/* Source URLs */}
            {(item.videoUrl || item.horsetelexUrl || item.equipeUrl) && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] leading-snug mt-2 pt-2 border-t border-gray-200">
                <span className="text-gray-500">Sources:</span>
                {item.videoUrl && (
                  <a
                    href={item.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                  >
                    <img src="/icons/youtube.svg" alt="" className="h-3 w-3" />
                    ClipMyHorse
                    <ExternalLink size={10} />
                  </a>
                )}
                {item.horsetelexUrl && (
                  <a
                    href={item.horsetelexUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                  >
                    HorseTelex
                    <ExternalLink size={10} />
                  </a>
                )}
                {item.equipeUrl && (
                  <a
                    href={item.equipeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                  >
                    Equipe
                    <ExternalLink size={10} />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const CompetitionResultsTable: React.FC<{
  title: string;
  items: PerformanceListItem[];
  emptyLabel: string;
  limit?: number;
  onLoadMore?: () => void;
  canLoadMore?: boolean;
  headerExtra?: React.ReactNode;
  // Insert a rider-name divider above each run of rows with the same rider.
  // Only meaningful when items are in date order.
  showRiderChanges?: boolean;
}> = ({
  title,
  items,
  emptyLabel,
  limit,
  onLoadMore,
  canLoadMore,
  headerExtra,
  showRiderChanges,
}) => {
  const visible = typeof limit === 'number' ? items.slice(0, limit) : items;
  let prevRider: string | null = null;

  return (
    <div className="py-3">
      {/* Title */}
      <h3 className="text-[16px] font-semibold text-[#132B64] text-center mb-4">{title}</h3>

      {headerExtra}

      {items.length === 0 ? (
        <p className="text-sm text-slate-600 text-center">{emptyLabel}</p>
      ) : (
        <>
          {/* Header row */}
          <div
            className="flex items-center h-[20px] text-[11px] text-slate-500 mb-2 px-1"
          >
            <span className="w-[30px]" />
            <div
              className="grid flex-1 min-w-0 items-center gap-1"
              style={{ gridTemplateColumns: '0.6fr 0.6fr 0.2fr 0.9fr' }}
            >
              <span>Date</span>
              <span>Height</span>
              <span>Rank</span>
              <span>Score</span>
            </div>
            <div className="w-[36px]" />
          </div>

          {/* Data rows - no gap between them */}
          <div className="overflow-hidden">
            {visible.map((item, idx) => {
              const riderChanged = showRiderChanges && !!item.rider && item.rider !== prevRider;
              if (item.rider) prevRider = item.rider;
              return (
                <React.Fragment key={`${item.title}-${item.date}-${item.rank ?? ''}-${idx}`}>
                  {riderChanged && (
                    <div className={`flex items-center gap-2 px-1 pb-1 text-[11px] font-semibold text-slate-500 ${idx > 0 ? 'pt-3' : ''}`}>
                      <span className="truncate">{item.rider}</span>
                      <span className="flex-1 border-t border-slate-200" />
                    </div>
                  )}
                  <CompetitionTableRow
                    item={item}
                    isFirst={idx === 0 || riderChanged}
                    isLast={idx === visible.length - 1}
                  />
                </React.Fragment>
              );
            })}
          </div>

          {canLoadMore && onLoadMore && (
            <button
              className="mt-3 text-sm font-semibold text-[#132B64] hover:text-[#0F244F] block mx-auto"
              onClick={onLoadMore}
            >
              Load more…
            </button>
          )}
        </>
      )}
    </div>
  );
};

const baseTimeScale = {
  type: 'time' as const,
  time: {
    unit: 'month' as const,
    tooltipFormat: 'MMM yyyy',
    displayFormats: { month: 'MMM yy' },
  },
  grid: { display: false },
  ticks: {
    color: COLOR_NEUTRAL,
    font: { size: 10 },
    maxRotation: 45,
    minRotation: 45,
  },
};

// Points further apart than this are not joined: a break of more than 3 months without rounds
// (e.g. Jan -> Jun) shows as a gap in the line, while up to 3 off months (Jan -> May) stays connected.
const MAX_CONNECTED_GAP_MS = 136 * 24 * 60 * 60 * 1000;

const buildLineDataset = (
  label: string,
  months: string[],
  values: Array<number | null>,
  color: string
) => ({
  label,
  data: months.map((month, idx) => ({
    x: `${month}-01`,
    y: values[idx] ?? null,
  })),
  spanGaps: MAX_CONNECTED_GAP_MS,
  borderColor: color,
  backgroundColor: color,
  pointRadius: 2,
  borderWidth: 1.5,
  tension: 0.1,
});

const ClearRoundRateChart: React.FC<{ data: PerformanceData }> = ({ data }) => {
  // Count valid data points for the "Limited data" annotation
  const roundCount = useMemo(() => {
    return data.meanClearRoundMovingAvg.filter((v) => v !== null && v !== undefined).length;
  }, [data.meanClearRoundMovingAvg]);

  const isLimitedData = roundCount < 10;
  const windowMonths = data.movingAvgWindow ?? 6;

  const chartData = useMemo(
    () => ({
      datasets: [
        buildLineDataset('Horse clear round rate, rolling', data.months, data.meanClearRoundMovingAvg, COLOR_PRIMARY),
        buildLineDataset(
          'Class average clear round rate, rolling',
          data.months,
          data.meanClassClearRoundRateMovingAvg,
          COLOR_SECONDARY
        ),
        // the month on its own, as unconnected dots: with a handful of rounds it jumps around
        {
          ...buildLineDataset(MONTHLY_CLEAR_LABEL, data.months, data.monthlyClearRoundRate, COLOR_PRIMARY),
          showLine: false,
          pointRadius: 4,
          pointStyle: 'rectRot' as const,
          pointBorderColor: COLOR_PRIMARY_DARK,
          pointBorderWidth: 1.5,
        },
      ],
    }),
    [data]
  );

  // Months with rounds but no clear-round data (e.g. results without faults) would otherwise
  // stretch the time axis to empty space: span only the months where some series has a value.
  const xRange = useMemo(() => {
    const hasValue = (idx: number) =>
      [data.meanClearRoundMovingAvg, data.meanClassClearRoundRateMovingAvg, data.monthlyClearRoundRate].some(
        (series) => series[idx] !== null && series[idx] !== undefined
      );
    const first = data.months.findIndex((_, idx) => hasValue(idx));
    if (first === -1) return {};
    let last = data.months.length - 1;
    while (!hasValue(last)) last--;
    // room on each side so the first and last diamonds are drawn whole: a share of the span, since a
    // fixed number of days shrinks to a pixel or two on a horse with many years of results
    const start = new Date(`${data.months[first]}-01T00:00:00`).getTime();
    const end = new Date(`${data.months[last]}-01T00:00:00`).getTime();
    const pad = Math.max((end - start) * 0.03, 15 * 24 * 60 * 60 * 1000);
    return { min: start - pad, max: end + pad };
  }, [data]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: { ...baseTimeScale, ...xRange },
        y: {
          // a little room beyond 0-100% so diamonds at the edges are drawn whole; ticks stay on the quarters
          min: -0.06,
          max: 1.06,
          afterBuildTicks: (scale: any) => {
            scale.ticks = [0, 0.25, 0.5, 0.75, 1].map((value) => ({ value }));
          },
          grid: { display: false },
          ticks: {
            color: COLOR_NEUTRAL,
            callback: (value: string | number) => `${Math.round(Number(value) * 100)}%`,
            font: { size: 10 },
            stepSize: 0.25,
          },
        },
      },
      plugins: {
        legend: {
          display: true,
          position: 'bottom' as const,
          labels: { usePointStyle: true, boxWidth: 8, font: { size: 10 } },
        },
        title: {
          display: true,
          text: 'Clear Round Rate vs Class Average',
          font: { size: 14, weight: 600 },
          color: COLOR_NEUTRAL,
        },
        subtitle: {
          display: true,
          text: `Lines: rolling average of all rounds in the ${windowMonths} months up to each point. Diamonds: that month alone`,
          font: { size: 10 },
          color: '#9CA3AF',
          padding: { bottom: 6 },
        },
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              const value = ctx.parsed.y as number;
              if (!Number.isFinite(value)) return '';
              const label = `${ctx.dataset.label}: ${Math.round(value * 100)}%`;
              if (ctx.dataset.label !== MONTHLY_CLEAR_LABEL) return label;
              const rounds = data.monthlyNumRounds[ctx.dataIndex] ?? 0;
              return `${label} (${Math.round(value * rounds)} of ${rounds} ${rounds === 1 ? 'round' : 'rounds'})`;
            },
          },
        },
      },
    }),
    [windowMonths, data.monthlyNumRounds, xRange]
  );

  return (
    <div className="relative">
      <div className="h-72">
        <Line data={chartData} options={options} />
      </div>
      {isLimitedData && (
        <p className="text-xs text-gray-500 text-center mt-1">
          Limited data (N = {roundCount} {roundCount === 1 ? 'month' : 'months'})
        </p>
      )}
    </div>
  );
};

const buildBarDataset = (label: string, months: string[], values: Array<number | null>) => ({
  label,
  data: months.map((month, idx) => ({
    x: `${month}-01`,
    y: values[idx] ?? null,
  })),
  backgroundColor: COLOR_PRIMARY,
  borderRadius: 6,
});

const ObstacleHeightChart: React.FC<{ data: PerformanceData }> = ({ data }) => {
  const chartData = useMemo(
    () => ({
      datasets: [
        {
          label: 'Max obstacle height',
          data: data.months.map((month, idx) => ({
            x: `${month}-01`,
            y: data.maxObstacleHeight[idx] ?? null,
          })),
          spanGaps: true,
          borderColor: COLOR_PRIMARY,
          backgroundColor: COLOR_PRIMARY,
          pointRadius: 3,
          borderWidth: 2,
          stepped: 'after' as const, // Step chart
          tension: 0,
          fill: false,
        },
      ],
    }),
    [data]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: baseTimeScale,
        y: {
          grid: { display: false },
          min: 100,
          ticks: {
            color: COLOR_NEUTRAL,
            callback: (value: string | number) => `${value} cm`,
            font: { size: 10 },
          },
        },
      },
      plugins: {
        legend: { display: false },
        title: {
          display: true,
          text: 'Level Progression (Obstacle Height)',
          font: { size: 14, weight: 600 },
          color: COLOR_NEUTRAL,
        },
      },
      elements: {
        point: { radius: 3, hitRadius: 6 },
      },
    }),
    []
  );

  return (
    <div className="h-64">
      <Line data={chartData} options={options} />
    </div>
  );
};

const _PrizeMoneyChart: React.FC<{ data: PerformanceData }> = ({ data }) => {
  const chartData = useMemo(
    () => ({
      datasets: [buildBarDataset('Prize money', data.months, data.sumPrizeInEuro)],
    }),
    [data]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: baseTimeScale,
        y: {
          grid: { display: false },
          ticks: {
            color: COLOR_NEUTRAL,
            callback: (value: string | number) => `€${Number(value) >= 1000 ? `${Math.round(Number(value) / 100) / 10}k` : value}`,
            font: { size: 10 },
          },
        },
      },
      plugins: {
        legend: { display: false },
        title: {
          display: true,
          text: 'Prize money',
          font: { size: 14, weight: 600 },
          color: COLOR_NEUTRAL,
        },
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              const value = ctx.parsed.y as number;
              if (!Number.isFinite(value)) return '';
              return `Prize: €${value.toLocaleString()}`;
            },
          },
        },
      },
    }),
    []
  );

  return (
    <div className="h-64">
      <Bar data={chartData} options={options} />
    </div>
  );
};

// Fault bucket types and colors
type FaultBucket = 'clear' | 'minor' | 'major' | 'blowup';

const FAULT_BUCKET_COLORS: Record<FaultBucket, string> = {
  clear: '#7DCD85',    // Green (from Figma)
  minor: '#F5D466',    // Yellow (from Figma)
  major: '#EEA63B',    // Orange (from Figma)
  blowup: '#D56C61',   // Red (from Figma)
};

const FAULT_BUCKET_LABELS: Record<FaultBucket, string> = {
  clear: 'Clear (0)',
  minor: '1–4 faults',
  major: '5–12 faults',
  blowup: '13+ faults / EL',
};

const classifyFaultBucket = (entry: UnifiedClassEntry): FaultBucket => {
  const statusStr = entry.unified_status?.toString().toUpperCase() || '';
  const resultStr = entry.unified_result?.toString().toUpperCase() || '';

  // Check for EL/RET/DSQ status
  if (statusStr === 'EL' || statusStr === 'RET' || statusStr === 'DSQ' ||
      statusStr.includes('ELIM') || statusStr.includes('RETIRED') ||
      resultStr === 'EL' || resultStr === 'RET' || resultStr === 'DSQ') {
    return 'blowup';
  }

  // Use unified_first_round_faults for fault count
  const faults = entry.unified_first_round_faults;
  if (faults === null || faults === undefined) {
    // If no score data, check is_clear_round flag
    if (entry.unified_is_clear_round === true) return 'clear';
    return 'minor'; // Default fallback
  }

  if (faults >= 13) return 'blowup';
  if (faults >= 5) return 'major';
  if (faults >= 1) return 'minor';
  return 'clear';
};

interface PeriodFaultData {
  period: string;
  label: string;
  counts: Record<FaultBucket, number>;
  total: number;
}

const getQuarterFromMonth = (monthStr: string): { quarter: string; label: string } => {
  const [year, month] = monthStr.split('-').map(Number);
  if (!year || !month) return { quarter: monthStr, label: monthStr };
  const q = Math.ceil(month / 3);
  return {
    quarter: `${year}-Q${q}`,
    label: `Q${q} '${String(year).slice(2)}`,
  };
};

const aggregateFaultsByQuarter = (entries: UnifiedClassEntry[]): PeriodFaultData[] => {
  const periodMap = new Map<string, { label: string; counts: Record<FaultBucket, number> }>();

  entries.forEach((entry) => {
    if (isClipMyHorseOnlyNoScore(entry)) return;
    // Use unified_month field or extract from unified_class_date
    const month = entry.unified_month || entry.unified_class_date?.slice(0, 7);
    if (!month) return;

    const { quarter, label } = getQuarterFromMonth(month);
    const bucket = classifyFaultBucket(entry);

    if (!periodMap.has(quarter)) {
      periodMap.set(quarter, { label, counts: { clear: 0, minor: 0, major: 0, blowup: 0 } });
    }
    periodMap.get(quarter)!.counts[bucket]++;
  });

  // Convert to sorted array
  return Array.from(periodMap.entries())
    .map(([period, { label, counts }]) => ({
      period,
      label,
      counts,
      total: counts.clear + counts.minor + counts.major + counts.blowup,
    }))
    .sort((a, b) => a.period.localeCompare(b.period));
};

const FaultsDistributionChart: React.FC<{ data: PerformanceData }> = ({ data }) => {
  const periodData = useMemo(() => aggregateFaultsByQuarter(data.classEntries), [data.classEntries]);
  
  // Use a ref to always have access to the latest periodData in the plugin
  const periodDataRef = useRef(periodData);
  periodDataRef.current = periodData;
  
  const totalRounds = useMemo(
    () => periodData.reduce((sum, p) => sum + p.total, 0),
    [periodData]
  );
  
  const chartData = useMemo(() => {
    const buckets: FaultBucket[] = ['clear', 'minor', 'major', 'blowup'];
    
    return {
      labels: periodData.map((p) => p.label),
      datasets: buckets.map((bucket) => ({
        label: FAULT_BUCKET_LABELS[bucket],
        data: periodData.map((p) => {
          if (p.total === 0) return 0;
          return (p.counts[bucket] / p.total) * 100;
        }),
        backgroundColor: periodData.map(() => FAULT_BUCKET_COLORS[bucket]),
        borderColor: FAULT_BUCKET_COLORS[bucket],
        borderWidth: 1,
      })),
    };
  }, [periodData]);

  // Custom plugin to draw sample size numbers above each bar
  // Use ref to always access latest periodData
  const sampleSizePlugin = useMemo(
    () => ({
      id: 'sampleSizeLabels',
      afterDatasetsDraw: (chart: any) => {
        const { ctx } = chart;
        const currentPeriodData = periodDataRef.current;
        ctx.save();
        ctx.textAlign = 'center';
        ctx.font = '7px Inter, system-ui, sans-serif';
        
        // Get the top dataset meta to find the top of stacked bars
        const topMeta = chart.getDatasetMeta(chart.data.datasets.length - 1);
        
        currentPeriodData.forEach((p, idx) => {
          if (!topMeta.data[idx]) return;
          const bar = topMeta.data[idx];
          const x = bar.x;
          // Position just above the bar
          const y = bar.y - 3;
          
          ctx.fillStyle = p.total < 3 ? '#9CA3AF' : '#6B7280';
          ctx.fillText(p.total.toString(), x, y);
        });
        ctx.restore();
      },
    }),
    [] // No dependencies - plugin reads from ref
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          stacked: true,
          grid: { display: false },
          ticks: {
            color: COLOR_NEUTRAL,
            font: { size: 9 },
            maxRotation: 45,
            minRotation: 45,
            autoSkip: true,
            maxTicksLimit: 8,
          },
          afterFit: (axis: any) => {
            // Ensure first and last ticks are always shown
            if (axis.ticks && axis.ticks.length > 2) {
              const ticks = axis.ticks;
              // Mark first and last as not to be skipped
              ticks[0].skip = false;
              ticks[ticks.length - 1].skip = false;
            }
          },
        },
        y: {
          stacked: true,
          min: 0,
          max: 100,
          grid: { display: false },
          ticks: {
            color: COLOR_NEUTRAL,
            callback: (value: string | number) => `${value}%`,
            font: { size: 10 },
            stepSize: 25,
          },
        },
      },
      plugins: {
        legend: {
          display: true,
          position: 'bottom' as const,
          labels: {
            usePointStyle: true,
            boxWidth: 8,
            font: { size: 10 },
            padding: 12,
          },
        },
        title: {
          display: true,
          text: 'Faults Distribution Over Time',
          font: { size: 14, weight: 'bold' as const },
          color: COLOR_NEUTRAL,
        },
        subtitle: {
          display: true,
          text: 'Numbers above bars = entries per quarter',
          font: { size: 9, weight: 'normal' as const },
          color: '#9CA3AF',
          padding: { bottom: 16 },
        },
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              const value = ctx.raw as number;
              return `${ctx.dataset.label}: ${value.toFixed(1)}%`;
            },
          },
        },
      },
      layout: {
        padding: { top: 16, bottom: 4 },
      },
    }),
    []
  );

  if (periodData.length === 0) {
    return (
      <div className="h-72 flex items-center justify-center text-sm text-gray-500">
        No fault data available for this period.
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="h-80">
        <Bar data={chartData} options={options} plugins={[sampleSizePlugin]} />
      </div>
      {totalRounds < 10 && (
        <p className="text-xs text-gray-500 text-center mt-1">
          Limited data (N = {totalRounds} {totalRounds === 1 ? 'round' : 'rounds'} total)
        </p>
      )}
    </div>
  );
};

const PerformanceGraphs: React.FC<{ data: PerformanceData }> = ({ data }) => (
  <div className="space-y-4">
    <PerformanceCard title="Performance overview" alignCenter>
      <div className="space-y-8">
        <ClearRoundRateChart data={data} />
        <ObstacleHeightChart data={data} />
        <FaultsDistributionChart data={data} />
      </div>
    </PerformanceCard>
  </div>
);

const _PlaceholderCard: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <PerformanceCard title={title}>
    <p className="text-sm text-slate-600">{description}</p>
  </PerformanceCard>
);

/* ─── Horse Activity Heatmap ────────────────────────────────────────── */

const ACTIVITY_COLORS = ['#ebedf0', '#c6e48b', '#7DCD85', '#49a05e', '#2d6a3f'];

function getWeekOfMonth(date: Date): number {
  const day = date.getDate();
  return Math.min(Math.ceil(day / 7), 5);
}

function buildActivityGrid(entries: UnifiedClassEntry[]): {
  months: string[];
  grid: Map<string, number>;
  maxCount: number;
} {
  const counts = new Map<string, number>();
  let maxCount = 0;
  let earliest: string | null = null;

  for (const entry of entries) {
    if (!entry.unified_class_date) continue;
    const d = new Date(entry.unified_class_date);
    if (isNaN(d.getTime())) continue;

    const month = entry.unified_class_date.slice(0, 7);
    if (!earliest || month < earliest) earliest = month;
    const week = getWeekOfMonth(d);
    const key = `${month}-w${week}`;
    const next = (counts.get(key) || 0) + 1;
    counts.set(key, next);
    if (next > maxCount) maxCount = next;
  }

  if (!earliest) return { months: [], grid: counts, maxCount };

  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const months: string[] = [];
  let cursor = earliest;
  while (cursor <= currentMonth) {
    months.push(cursor);
    const [y, m] = cursor.split('-').map(Number);
    const next = m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
    cursor = next;
  }

  return { months, grid: counts, maxCount };
}

function activityLevel(count: number, maxCount: number): number {
  if (count === 0 || maxCount === 0) return 0;
  const ratio = count / maxCount;
  if (ratio <= 0.25) return 1;
  if (ratio <= 0.5) return 2;
  if (ratio <= 0.75) return 3;
  return 4;
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatMonthLabel(ym: string): string {
  const [y, m] = ym.split('-');
  return `${MONTH_SHORT[parseInt(m, 10) - 1]} '${y.slice(2)}`;
}

const HorseActivityHeatmap: React.FC<{ data: PerformanceData }> = ({ data }) => {
  const { months, grid, maxCount } = useMemo(() => buildActivityGrid(data.classEntries), [data.classEntries]);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
    }
  }, [months]);

  if (months.length === 0) return null;

  const weeks = [1, 2, 3, 4, 5];

  return (
    <PerformanceCard title="Competition activity">
      <div ref={scrollRef} style={{ overflowX: 'auto', overflowY: 'hidden' }}>
        <table
          role="grid"
          aria-readonly
          style={{ borderSpacing: 3, borderCollapse: 'separate' }}
        >
          <thead>
            <tr style={{ height: 14 }}>
              <td style={{ width: 28 }} />
              {months.map((m) => (
                <td
                  key={m}
                  className="text-center"
                  style={{ fontSize: 9, color: '#767676', padding: 0, whiteSpace: 'nowrap' }}
                >
                  {formatMonthLabel(m)}
                </td>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((w) => (
              <tr key={w} style={{ height: 11 }}>
                <td
                  style={{ fontSize: 9, color: '#767676', padding: '0 4px 0 0', textAlign: 'right', whiteSpace: 'nowrap' }}
                >
                  {w === 1 || w === 3 || w === 5 ? `W${w}` : ''}
                </td>
                {months.map((m) => {
                  const key = `${m}-w${w}`;
                  const count = grid.get(key) || 0;
                  const level = activityLevel(count, maxCount);
                  return (
                    <td
                      key={key}
                      role="gridcell"
                      title={
                        count === 0
                          ? `No competitions – Week ${w}, ${formatMonthLabel(m)}`
                          : `${count} competition${count !== 1 ? 's' : ''} – Week ${w}, ${formatMonthLabel(m)}`
                      }
                      style={{
                        width: 11,
                        height: 11,
                        borderRadius: 2,
                        backgroundColor: ACTIVITY_COLORS[level],
                        padding: 0,
                        cursor: 'default',
                      }}
                    />
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend — outside scroll container */}
      <div className="flex items-center justify-end gap-1 mt-2" style={{ fontSize: 9, color: '#767676' }}>
        <span>Less</span>
        {ACTIVITY_COLORS.map((c, i) => (
          <span
            key={i}
            style={{
              display: 'inline-block',
              width: 10,
              height: 10,
              borderRadius: 2,
              backgroundColor: c,
            }}
          />
        ))}
        <span>More</span>
      </div>
    </PerformanceCard>
  );
};

const logoPaths: Record<string, string> = {
  horsetelex: '/logos/horsetelex_logo_with_text_v2.png',
  horsetelexResults: '/logos/horsetelexresults_logo_with_text_v2.png',
  clipmyhorse: '/logos/cmh_logo_with_text_v2.png',
  fei: '/logos/fei_logo.png',
};

const PublicLinks: React.FC<{ data: PerformanceData }> = ({ data }) => {
  const links = [
    data.horsetelexId
      ? {
          key: 'horsetelex',
          label: 'HorseTelex',
          url: `https://www.horsetelex.com/horses/pedigree/${data.horsetelexId}`,
        }
      : null,
    data.horsetelexId
      ? {
          key: 'horsetelexResults',
          label: 'HorseTelex Results',
          url: `https://www.horsetelexresults.com/rankings/${data.horsetelexId}`,
        }
      : null,
    data.cmhUrl
      ? {
          key: 'clipmyhorse',
          label: 'ClipMyHorse',
          url: data.cmhUrl,
        }
      : null,
    data.feiId
      ? {
          key: 'fei',
          label: 'FEI',
          url: `https://www.fei.org/horse/${data.feiId}`,
        }
      : null,
  ].filter(Boolean) as { key: string; label: string; url: string }[];

  if (links.length === 0) {
    return null;
  }

  return (
    <PerformanceCard title="Useful Links" alignCenter srLabel="Public profiles">
      <div className="flex flex-nowrap justify-between gap-2 w-full">
        {links.map((link) => {
          const logo = logoPaths[link.key];
          return (
            <a
              key={link.url}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center px-1.5 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 shadow-sm hover:shadow"
              style={{ minHeight: '48px' }}
            >
              {logo ? (
                <img
                  src={logo}
                  alt={link.label}
                  className="h-9 w-auto object-contain"
                />
              ) : (
                <ExternalLink size={18} className="text-blue-700" />
              )}
            </a>
          );
        })}
      </div>
    </PerformanceCard>
  );
};

const RidersHistoryTimeline: React.FC<{ riders: RiderEntry[] }> = ({ riders }) => {
  // Build timeline points: each rider's start date + final rider's end date
  // This creates N+1 circles for N rider periods
  const rawDates: Date[] = [];
  const displayDates: Date[] = []; // Original dates for display labels
  const riderForSegment: string[] = []; // rider name for segment between point i and i+1

  riders.forEach((r, idx) => {
    const startDate = r.startDate ? new Date(r.startDate) : new Date();
    rawDates.push(startDate);
    displayDates.push(startDate);
    riderForSegment.push(r.name);
    // Add end date as final circle for last rider
    if (idx === riders.length - 1 && r.endDate) {
      const endDate = new Date(r.endDate);
      rawDates.push(endDate);
      displayDates.push(endDate);
    }
  });

  // Spread dates for chart positioning (these may differ from actual dates)
  const adjustedDates = spreadRiderDates(rawDates, MIN_RIDER_DAY_GAP, MAX_RIDER_DAY_GAP);
  const hasLine = adjustedDates.length > 1;

  // Current rider marker on the most recent point (last)
  const lastIdx = adjustedDates.length - 1;

  const data = {
    datasets: [
      {
        label: 'Riders',
        data: adjustedDates.map((d, idx) => ({
          x: 0,
          y: d,
          displayDate: displayDates[idx], // Original date for label
          name: riderForSegment[idx] || '',
          current: idx === lastIdx,
        })),
        borderColor: '#7DCD85',
        backgroundColor: '#7DCD85',
        pointBackgroundColor: '#FFFFFF',
        pointBorderColor: '#7DCD85',
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 6,
        fill: false,
        tension: 0,
        showLine: hasLine,
        borderWidth: hasLine ? 2 : 0,
      },
    ],
  };

  const labelPlugin = {
    id: 'riderLabels',
    afterDatasetsDraw: (chart: any) => {
      const { ctx } = chart;
      const meta = chart.getDatasetMeta(0);
      const points = meta.data as any[];
      const dataArr = chart.data.datasets[0].data as any[];
      ctx.save();
      ctx.textAlign = 'left';
      ctx.fillStyle = '#7DCD85';
      ctx.font = '600 10px Inter, system-ui, -apple-system, sans-serif';
      const adjX = points[0]?.x + 12 || 12;

      // Draw date labels at each circle (using original dates, not adjusted positions)
      points.forEach((point: any, idx: number) => {
        const raw = dataArr[idx];
        const dateToShow = raw.displayDate || raw.y;
        const dateText = dateToShow ? formatMonthYear(dateToShow.toISOString().slice(0, 10)) : '';
        ctx.fillText(dateText, adjX, point.y + 4);
      });

      // Draw rider name labels at midpoints between consecutive circles
      // With reverse axis, higher y-pixel = older date (bottom), lower y-pixel = newer date (top)
      for (let i = 0; i < points.length - 1; i++) {
        const currY = points[i].y;
        const nextY = points[i + 1].y;
        const midY = (currY + nextY) / 2;
        const name = riderForSegment[i] || '';
        ctx.font = 'bold 10px Inter, system-ui, -apple-system, sans-serif';
        ctx.fillStyle = '#132B64';
        ctx.fillText(name, adjX, midY + 4);
        ctx.fillStyle = '#7DCD85';
        ctx.font = '10px Inter, system-ui, -apple-system, sans-serif';
      }
      ctx.restore();
    },
  };

  // Compute axis bounds
  const sortedDates = [...adjustedDates].sort((a, b) => a.getTime() - b.getTime());
  const minDate = sortedDates[0]
    ? new Date(sortedDates[0].getTime() - MIN_RIDER_DAY_GAP * DAY_MS)
    : undefined;
  const maxDate = sortedDates[sortedDates.length - 1]
    ? new Date(sortedDates[sortedDates.length - 1].getTime() + MIN_RIDER_DAY_GAP * DAY_MS)
    : undefined;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: () => '',
          label: (ctx: any) => ctx.raw?.name || '',
        },
      },
    },
    scales: {
      x: {
        type: 'linear' as const,
        display: false,
        grid: { display: false },
        border: { display: false },
        min: -1,
        max: 1,
      },
      y: {
        type: 'time' as const,
        time: { unit: 'month' as const, tooltipFormat: 'MMM yyyy', displayFormats: { month: 'MMM yy' } },
        grid: { display: false },
        border: { display: false },
        reverse: false,
        ticks: { display: false, padding: 6 },
        min: minDate,
        max: maxDate,
      },
    },
    elements: {
      line: { borderWidth: 2 },
      point: { hitRadius: 6 },
    },
    layout: {
      padding: { left: 0, right: 48, top: 16, bottom: 32 },
    },
  };

  const riderChartHeight = Math.max(150, 100 + adjustedDates.length * 80);

  return (
    <PerformanceCard title="Riders History" alignCenter>
      <div className="px-0" style={{ height: riderChartHeight, marginLeft: '-20px' }}>
        <Line data={data} options={options as any} plugins={[labelPlugin]} />
      </div>
    </PerformanceCard>
  );
};

// Period Filter UI Component
const PeriodFilterBar: React.FC<{
  selected: PeriodFilter;
  onChange: (period: PeriodFilter) => void;
  isSticky?: boolean;
  barRef?: React.Ref<HTMLDivElement>;
}> = ({ selected, onChange, isSticky, barRef }) => {
  const periods: PeriodFilter[] = ['6m', '1y', '3y', 'max'];
  return (
    <div
      ref={barRef}
      className={`${isSticky ? 'sticky top-0 z-20 bg-white' : ''} flex items-center justify-between px-4 py-3 mb-4`}
    >
      <span className="text-sm font-semibold text-gray-700">Performance Period</span>
      <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
        {periods.map((p) => (
          <button
            key={p}
            onClick={() => onChange(p)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              selected === p
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {PERIOD_LABELS[p]}
          </button>
        ))}
      </div>
    </div>
  );
};

// Competition history data-scope toggle
const DATA_SCOPE_LABELS: Record<CompetitionHistoryScope, string> = {
  all: 'All entries',
  results: 'With results',
};

const DataScopeFilterBar: React.FC<{
  selected: CompetitionHistoryScope;
  onChange: (scope: CompetitionHistoryScope) => void;
  isSticky?: boolean;
  barRef?: React.Ref<HTMLDivElement>;
}> = ({ selected, onChange, isSticky, barRef }) => {
  const scopes: CompetitionHistoryScope[] = ['all', 'results'];
  return (
    <div
      ref={barRef}
      className={`${isSticky ? 'sticky top-0 z-20 bg-white' : ''} flex items-center justify-between px-4 py-3 mb-4`}
    >
      <span className="text-sm font-semibold text-gray-700">Show</span>
      <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
        {scopes.map((s) => (
          <button
            key={s}
            onClick={() => onChange(s)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              selected === s
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {DATA_SCOPE_LABELS[s]}
          </button>
        ))}
      </div>
    </div>
  );
};

export const HorsePerformanceSection: React.FC<HorsePerformanceSectionProps> = ({
  horseId,
  horseName,
  horse,
  onMaxLevelChange,
}) => {
  const [state, setState] = useState<LoadState>('idle');
  const [performance, setPerformance] = useState<PerformanceData | null>(null);
  const [fullLimit, setFullLimit] = useState(10);
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('max');
  const [dataScope, setDataScope] = useState<CompetitionHistoryScope>('results');
  const [isPeriodSticky, setIsPeriodSticky] = useState<boolean>(true);
  const [isDataScopeSticky, setIsDataScopeSticky] = useState<boolean>(false);
  const periodBarRef = useRef<HTMLDivElement | null>(null);
  const dataScopeSentinelRef = useRef<HTMLDivElement | null>(null);
  const historicalSentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isPerformanceDataEnabled()) {
      setState('disabled');
      return;
    }
    if (!horseId) {
      setState('error');
      return;
    }

    setState('loading');
    loadPerformanceData(horseId)
      .then((result) => {
        if (!result) {
          setState('error');
          return;
        }
        setPerformance(result);
        setState('ready');
      })
      .catch((error) => {
        console.warn('Performance load failed', error);
        setState('error');
      });
  }, [horseId]);

  useEffect(() => {
    if (!performance || !onMaxLevelChange) return;
    const entryHeights = performance.classEntries
      .map((e) => (typeof e.unified_height_cm === 'number' ? e.unified_height_cm : null))
      .filter((v): v is number => v !== null && Number.isFinite(v));
    const monthlyHeights = performance.maxObstacleHeight.filter((v): v is number => v !== null && Number.isFinite(v));
    const maxVal = Math.max(...entryHeights, ...monthlyHeights, -Infinity);
    onMaxLevelChange(Number.isFinite(maxVal) && maxVal !== -Infinity ? maxVal : null);
  }, [performance, onMaxLevelChange]);

  // Manage which sticky bar is shown based on scroll position.
  // - Above the data-scope sentinel: period filter sticks.
  // - Between data-scope and historical sentinels: data-scope toggle sticks (period unsticks).
  // - Past historical sentinel: both unstick.
  useEffect(() => {
    const handleScroll = () => {
      const dataScopeSentinel = dataScopeSentinelRef.current;
      const historicalSentinel = historicalSentinelRef.current;
      if (!dataScopeSentinel || !historicalSentinel) return;

      const dataScopeTop = dataScopeSentinel.getBoundingClientRect().top;
      const historicalTop = historicalSentinel.getBoundingClientRect().top;

      const pastHistorical = historicalTop <= 20;
      const pastDataScope = dataScopeTop <= 20;

      setIsPeriodSticky(!pastDataScope);
      setIsDataScopeSticky(pastDataScope && !pastHistorical);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Apply period filter to performance data (must be called before early returns)
  const filteredPerformance = useMemo(
    () => (performance ? filterPerformanceData(performance, periodFilter) : null),
    [performance, periodFilter]
  );

  const highlights = useMemo(() => (filteredPerformance ? buildHighlights(filteredPerformance) : []), [filteredPerformance]);
  const lowlights = useMemo(() => (filteredPerformance ? buildLowlights(filteredPerformance) : []), [filteredPerformance]);
  // Competition history is NOT affected by the period filter — uses full performance data with its own scope toggle.
  const fullResults = useMemo(
    () => (performance ? buildFullResults(performance, dataScope) : []),
    [performance, dataScope]
  );
  const riderHistory = useMemo(() => (performance ? buildRiderHistory(performance) : []), [performance]); // Full history, not filtered
  const haveGraphs = useMemo(() => hasPerformanceSeries(filteredPerformance), [filteredPerformance]);

  if (state === 'disabled' || !horseId) {
    return null;
  }

  if (state === 'loading' || state === 'idle') {
    return (
      <section className="mt-8 space-y-4">
        <div className="flex items-center space-x-2 text-sm text-slate-700">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
          <span>Loading performance data…</span>
        </div>
      </section>
    );
  }

  if (state === 'error' || !performance || !filteredPerformance) {
    return (
      <section className="mt-8">
        <PerformanceCard title="Performance data">
          <p className="text-sm text-slate-600">
            No performance data available for this horse in the current environment.
          </p>
        </PerformanceCard>
      </section>
    );
  }

  return (
    <section className="mt-8 space-y-6">
      {/* Period Filter */}
      <PeriodFilterBar
        selected={periodFilter}
        onChange={setPeriodFilter}
        isSticky={isPeriodSticky}
        barRef={periodBarRef}
      />

      {/* Performance Data Section (affected by period filter) */}
      <div className="space-y-4">
        <CompetitionResultsTable
          title="Highlights"
          items={highlights}
          emptyLabel={periodFilter === 'max' ? "No highlights available with video." : `No highlights in the last ${PERIOD_LABELS[periodFilter]}.`}
        />
        <CompetitionResultsTable
          title="Lowlights"
          items={lowlights}
          emptyLabel={periodFilter === 'max' ? "No lowlights available with video." : `No lowlights in the last ${PERIOD_LABELS[periodFilter]}.`}
        />
      </div>

      {haveGraphs && <PerformanceGraphs data={filteredPerformance} />}

      {/* Activity + Competition history block (NOT affected by period filter) */}
      <div ref={dataScopeSentinelRef} className="h-px" aria-hidden="true" />
      <div className="pt-4 border-t border-gray-200 space-y-6">
        <HorseActivityHeatmap data={performance} />
        <CompetitionResultsTable
          title="Competition history"
          items={fullResults}
          showRiderChanges
          emptyLabel={
            dataScope === 'all'
              ? 'No class entries available.'
              : 'No class results available.'
          }
          limit={fullLimit}
          canLoadMore={fullResults.length > fullLimit}
          onLoadMore={() => setFullLimit((prev) => prev + 20)}
          headerExtra={
            <DataScopeFilterBar
              selected={dataScope}
              onChange={setDataScope}
              isSticky={isDataScopeSticky}
            />
          }
        />
      </div>

      {/* Historical Records Section (NOT affected by period filter) */}
      <div ref={historicalSentinelRef} className="h-px" aria-hidden="true" />
      <div className="space-y-4">
        <RidersHistoryTimeline riders={riderHistory} />
        {horse && <HorseOwnersHistory horse={horse} />}
      </div>

      <PublicLinks data={performance} />
    </section>
  );
};

export default HorsePerformanceSection;

